// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import $ from 'jquery';
import ui from 'jquery-ui';
import layout from 'jquery-layout';
import myw from 'myWorld/base/core';
import Layout from 'myWorld/layouts/layout';
import softKeyboardInput from 'myWorld/controls/softKeyboardInput';
import desktopHtml from 'text!html/desktop.html';
import 'myWorld/base/util';
import 'myWorld/base/viewManager';

myw.DesktopLayout = Layout.extend('DesktopLayout', {
    options: {
        viewManagerMaxItemsVisible: 2,

        eastEnabled: false,
        eastClosed: true,
        eastResizable: true,
        eastPanelSize: 350,

        westClosed: false,
        westPanelMinimumSize: 305, //enough for the buttons on the details panel when displaying a feature
        //calculate west panel initial size: a fourth of screen width, with max of 350 (minimum is controlled separately)
        westPanelInitialSize() {
            return Math.min(Math.floor($(window).width() / 4), 350);
        }
    },

    /**
     * @class Layout for a standard web browser client environment.
     * This includes desktop computers, laptops and tablets.
     * The registered controls will be instantiated when the layout is applied.
     * When layout sections are resized (either by a window resize or by collapsing/expanding sections), controls will be informed
     * via a call to method 'invalidateSize()' (if they implement it). This allows the controls to set sizes of internal elements (ie. for scrolling)
     * @param {desktopLayoutOptions} options
     * @extends {Layout}
     * @constructs
     */
    initialize(owner, options) {
        myw.Layout.prototype.initialize.call(this, owner, options);
    },

    /**
     * Initializes the body layout, map, viewManager and footer
     * @return {Promise<GeoMapControl>}
     */
    initUI() {
        if (myw.isTouchDevice) {
            $('body').addClass('touch-enabled');

            //  Adds the iOS13 class to the body if required
            //  ENH: Centralise the checks for iOS13
            const isIOS13 =
                (navigator.platform == 'iPad' && navigator.userAgent.match(/ CPU OS 13/)) ||
                (navigator.platform == 'iPhone' && navigator.userAgent.match(/ CPU iPhone OS 13/));
            if (isIOS13) {
                const body = document.getElementsByTagName('body')[0];
                body.classList.add('ios13');
            }
        }
        //load body html
        this.$el.html(desktopHtml);

        return myw.localisation.ready.then(() => {
            //we set the body layout early because the sizes of elements may be used by other
            //initializations (example: calculating the initial map bounds is dependent on the size
            //of the canvas element)
            this.setBodyLayout(this.app); //needs messages to be ready
            $('#logo').attr('title', this.msg('home_page'));

            this.addFooterAndTitleInfo(this); //needs messages to be ready

            //initialize the geographical map. Needs to happen after setBodyLayout()
            const map = new this.options.GeoMapControl(this.app, this.options.mapDivId);

            this.mapViewManager = new myw.ViewManager(this.options.viewManagerMaxItemsVisible);
            this.mapViewManager.register('map', map, true);
            this.mapViewManager.show('map');

            return map;
        });
    },

    /**
     * Add the Application name, username and the logout link to the footer
     */
    addFooterAndTitleInfo() {
        window.document.title = `myWorld ${this.app.externalName}`;
        //Adding the username of the logged-in user and logout link to the DOM
        $('#user').append(
            `<span id='username'>[${this.app.externalName}] ${myw.currentUser.username} </span>`
        );

        if (myw.currentUser.autoLogin !== true) {
            $('#user').append(
                `<a id="logout-link" href="logout?application=${this.app.name}"">${myw.msg(
                    'Application',
                    'logout_footer'
                )}</a>`
            );
        }

        const builtByEl = this.$('#built_by_footer');
        builtByEl.html(myw.msg('Application', 'built_by_footer'));
        this.$('img')
            .attr('src', myw.baseUrl + 'images/logos/IQGeo_Logo_main.svg');
    },

    setBodyLayout(app) {
        const triggerOpened = (paneName, jqueryElement, state) => {
            const control = this._getControlOnPane(paneName);
            if (control) control.visibilityChanged(true);
        };
        const triggerClosed = (paneName, jqueryElement, state) => {
            const control = this._getControlOnPane(paneName);
            if (control) control.visibilityChanged(false);
        };
        const triggerResized = (paneName, jqueryElement, state) => {
            const control = this._getControlOnPane(paneName);
            if (control) control.invalidateSize();
        };

        this.layout = this.$el.layout({
            applyDefaultStyles: false,
            south: {
                enableCursorHotkey: false,
                resizable: false,
                size: 18,
                closable: false
            },
            north: {
                enableCursorHotkey: false,
                resizable: false,
                size: 70,
                closable: false
            },
            //The East panel is hidden or shown according to the option 'eastEnabled'
            east: {
                spacing_open: 3,
                spacing_closed: 1,
                size: this.options.eastPanelSize,
                initHidden: !this.options.eastEnabled,
                resizeWhileDragging: true,
                slidable: false,
                closable: true,
                initClosed: this.options.eastClosed,
                resizable: this.options.eastResizable,
                resizeWithWindow: false,
                enableCursorHotkey: false,
                togglerTip_open: this.msg('hide_panel'),
                togglerTip_closed: this.msg('show_panel'),
                resizerTip: this.msg('resize_panel'),
                onopen_end: triggerOpened,
                onclose_end: triggerClosed,
                onresize: triggerResized
            },
            west: {
                spacing_open: 3,
                spacing_closed: 1,
                minSize: this.options.westPanelMinimumSize,
                size: _.result(this.options, 'westPanelInitialSize'),
                resizeWhileDragging: true,
                slidable: false,
                closable: true,
                initClosed: this.options.westClosed,
                resizable: true,
                resizeWithWindow: false,
                enableCursorHotkey: false,
                togglerTip_open: this.msg('hide_panel'),
                togglerTip_closed: this.msg('show_panel'),
                resizerTip: this.msg('resize_panel'),
                onopen_end: (...args) => {
                    if (this.controls.featureBriefControl) this.controls.featureBriefControl.hide();
                    triggerOpened(...args);
                },
                onclose_end: (...args) => {
                    if (this.controls.featureBriefControl) this.controls.featureBriefControl.show();
                    triggerClosed(...args);
                },
                onresize: triggerResized
            }
        });

        this.centerLayout = $('#layout-map-view').layout({
            applyDefaultStyles: false,
            south: {
                spacing_open: 3,
                spacing_closed: 1,
                size: '50%',
                initHidden: true,
                resizeWhileDragging: true,
                slidable: false,
                closable: true,
                resizable: true,
                resizeWithWindow: false,
                enableCursorHotkey: false,
                togglerTip_open: this.msg('hide_panel'),
                togglerTip_closed: this.msg('show_panel'),
                onopen_end: triggerOpened,
                onclose_end: triggerClosed,
                onresize: triggerResized
            },
            center: {
                onopen_end: triggerOpened,
                onclose_end: triggerClosed,
                onresize: (...args) => {
                    //inform the map that the size may have changed
                    if (app.map) app.map.invalidateSize();
                    //Resizes the visible map views
                    this.mapViewManager.invalidateSize();
                    triggerResized(...args);
                }
            }
        });

        this.state = this.layout.state;

        // For mobile devices:
        if (myw.isTouchDevice) {
            // Increase the focus area of panel hide/show buttons for easy usability
            $('.ui-layout-toggler').append("<div class='increase-toggler-focus-area'></div>");
        }
    },

    /**
     * Initializes the handlers for user events (mouse clicks, etc...)
     */
    initUserEventHandlers() {
        //setup handler for clicking the logo
        //when the logo's clicked execute the (only) action immediately
        $('#logo').click(() => {
            this.app.logoAction.action();
        });
    },

    /**
     * Opens the left panel and switches to the details tab
     */
    displayCurrentFeatureDetails() {
        this.layout.open('west');
        this.controls.tabControl.switchToTab('details');
    },

    _getControlOnPane(paneName) {
        const layout = paneName == 'south' ? this.centerLayout : this.layout,
            divId = layout[paneName].pane.attr('id');
        return _.find(this.controls, control => control.options.divId == divId);
    },

    /**
     * Opens the specified pane by delegating to the appropriate JQuery UI Layout object
     * @param  {string} paneName
     */
    open(paneName) {
        //get which jquery layout to use
        const layout = paneName == 'south' ? this.centerLayout : this.layout;
        //pass on the request
        layout.open(...arguments);
    },
    /**
     * Closes the specified pane by delegating to the appropriate JQuery UI Layout object
     * @param  {string} paneName
     */
    close(paneName) {
        //get which jquery layout to use
        let layout;
        if (paneName == 'south') {
            layout = this.centerLayout;
            layout.hide('south');
        } else {
            layout = this.layout;
        }
        //pass on the request
        layout.close(...arguments);
    },

    getState() {
        return {
            eastClosed: this.layout.state.east.isClosed,
            westClosed: this.layout.state.west.isClosed,
            controlsState: this.getChildrenState()
        };
    }
});

/**
 * Options to specify when creating a DesktopLayout
 * @typedef desktopLayoutOptions
 * @property {string}                       mapDivId                   Id of the div where the main map should be created
 * @property {boolean}                      eastEnabled=false          Whether a panel on the east should be added or not
 * @property {boolean}                      eastClosed=true            Whether the east panel should start closed(collapsed) or not
 * @property {boolean}                      eastResizable=true         Whether the east panel can be resized by the user or not
 * @property {integer}                      eastPanelSize=350          Size in pixels of east panel
 * @property {boolean}                      westClose=false            Whether the west panel should start closed(collapsed) or not
 * @property {integer    }                  westPanelMinimumSize=305   Minimum width in pixels for the west panel. Default is 305
 * @property {integer|function}             westPanelInitialSize       Initial width of west panel in pixels. Can be a function that returns the value. <br/>
 *                                                                     Default is a function that returns a fourth of the page width, with minimum of 275 and maximum of 350
 * @property {object<controlDefinition>}    controls   Control definitions, keyed on control name
 */

export default myw.DesktopLayout;
